import type { BuildQuoteResult } from '../../lib/buildQuote/types';
import { isConsultationProjectType } from '../../lib/buildQuote/types';
import { formatUsd } from '../../lib/buildQuote/format';
import { Sparkles } from 'lucide-react';

type Props = {
  quote: BuildQuoteResult;
  showDefense?: boolean;
};

export default function BuildQuoteCard({ quote, showDefense = true }: Props) {
  const consultationOnly = isConsultationProjectType(quote.projectType);

  return (
    <div className="rounded-2xl border border-white/12 bg-white/[0.04] p-6 sm:p-8">
      <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
        Your quote
        {quote.llmGenerated && (
          <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-white/60">
            <Sparkles className="h-3 w-3" />
            AI estimate
          </span>
        )}
      </p>
      <h3 className="mt-2 text-2xl font-bold sm:text-3xl">
        {formatUsd(quote.currentTotalCents)}
        {quote.discountPercent > 0 && (
          <span className="ml-2 text-base font-normal text-white/45 line-through">
            {formatUsd(quote.baseTotalCents)}
          </span>
        )}
      </h3>
      <p className="mt-2 text-sm text-white/55">
        {quote.projectTypeLabel} · {quote.timelineWeeks}
      </p>
      {quote.quoteRationale && (
        <p className="mt-2 text-sm text-white/45">{quote.quoteRationale}</p>
      )}

      <ul className="mt-6 space-y-2 border-t border-white/10 pt-6">
        {quote.lineItems.map((item) => (
          <li
            key={item.label}
            className="flex justify-between gap-4 text-sm text-white/65"
          >
            <span>{item.label}</span>
            <span className="shrink-0 text-white/80">{formatUsd(item.amountCents)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <p className="text-xs text-white/45">
          {consultationOnly ? 'Payment' : 'Payment options'}
        </p>
        {consultationOnly ? (
          <div className="mt-3">
            <p className="text-xs text-white/40">Pay in full</p>
            <p className="mt-1 text-lg font-semibold">{formatUsd(quote.currentTotalCents)}</p>
            <p className="mt-2 text-xs text-white/40">
              Consultations are paid in full at Stripe checkout.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs text-white/40">30% deposit</p>
                <p className="mt-1 text-lg font-semibold">{formatUsd(quote.depositCents)}</p>
              </div>
              <div>
                <p className="text-xs text-white/40">Pay in full</p>
                <p className="mt-1 text-lg font-semibold">{formatUsd(quote.currentTotalCents)}</p>
              </div>
            </div>
            <p className="mt-2 text-xs text-white/40">
              Stripe checkout is generated dynamically from this quote.
            </p>
          </>
        )}
      </div>

      {showDefense && quote.valueDefense.length > 0 && (
        <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <p className="text-xs font-medium text-white/50">Why this investment</p>
          <ul className="mt-2 space-y-2 text-sm text-white/55">
            {quote.valueDefense.map((line) => (
              <li key={line}>• {line}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
